import { useState } from "react";
import { useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { trpc } from "@/lib/trpc";
import { toast } from "sonner";
import { APP_TITLE } from "@/const";
import { BrandLogo } from "@/components/BrandLogo";
import { useTranslation } from "@/i18n";
import { useSetupCheck } from "@/hooks/useSetupCheck";
import { ShopLanguageSelector } from "@/components/ShopLanguageSelector";
import { Shield, ArrowLeft } from "lucide-react";

export default function Login() {
  const { t } = useTranslation();
  const { checked } = useSetupCheck();
  const [, setLocation] = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [requires2FA, setRequires2FA] = useState(false);
  const [userId, setUserId] = useState<number | null>(null);
  const [twoFactorCode, setTwoFactorCode] = useState("");
  const [rememberDevice, setRememberDevice] = useState(false);

  const utils = trpc.useUtils();

  const getLocalizedErrorMessage = (message?: string) => {
    if (!message) {
      return t.login.loginFailed;
    }

    const normalized = message.toLowerCase();
    if (normalized.includes("invalid email or password")) {
      return t.login.invalidCredentials;
    }
    if (
      normalized.includes("account is not activated") ||
      normalized.includes("please contact admin")
    ) {
      return t.auth.accountNotActivated;
    }

    return message;
  };
  
  const loginMutation = trpc.auth.login.useMutation({
    onSuccess: async (data) => {
      if (data.requiresTwoFactor) {
        // 2FA is required, show 2FA input
        setRequires2FA(true);
        setUserId(data.userId);
        setIsLoading(false);
        toast.info(t.settings.twoFactorEnterCode);
      } else {
        // No 2FA, login successful
        toast.success(t.login.loginSuccess);
        await utils.auth.me.invalidate();
        setLocation("/");
      }
    },
    onError: (error) => {
      toast.error(getLocalizedErrorMessage(error.message));
      setIsLoading(false);
    },
  });

  const verify2FAMutation = trpc.auth.twoFactor.verify.useMutation({
    onSuccess: async () => {
      toast.success(t.login.loginSuccess);
      await utils.auth.me.invalidate();
      setLocation("/");
    },
    onError: (error) => {
      toast.error(t.settings.twoFactorInvalidCode);
      setIsLoading(false);
      setTwoFactorCode("");
    },
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    loginMutation.mutate({ email, password, rememberDevice });
  };

  const handle2FASubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!userId || twoFactorCode.length < 6) {
      toast.error(t.settings.twoFactorEnterCode);
      return;
    }
    setIsLoading(true);
    verify2FAMutation.mutate({
      userId,
      token: twoFactorCode,
    });
  };

  const handleBackToLogin = () => {
    setRequires2FA(false);
    setUserId(null);
    setTwoFactorCode("");
    setPassword("");
    setIsLoading(false);
  };

  if (!checked) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen relative flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 px-4">
      <div className="absolute top-6 right-6">
        <ShopLanguageSelector />
      </div>
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1 text-center">
          <div className="flex justify-center mb-4">
            <BrandLogo size={64} rounded showName />
          </div>
          {requires2FA ? (
            <>
              <div className="flex items-center justify-center gap-2">
                <Shield className="h-6 w-6 text-primary" />
                <CardTitle className="text-2xl font-bold">{t.settings.twoFactorLoginTitle}</CardTitle>
              </div>
              <CardDescription>
                {t.settings.twoFactorLoginDescription}
              </CardDescription>
            </>
          ) : (
            <>
              <CardTitle className="text-2xl font-bold">{t.login.title}</CardTitle>
              <CardDescription>
                {t.login.subtitle.replace('{appTitle}', APP_TITLE)}
              </CardDescription>
            </>
          )}
        </CardHeader>
        
        {requires2FA ? (
          <form onSubmit={handle2FASubmit}>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="twoFactorCode">{t.settings.twoFactorLoginCode}</Label>
                <Input
                  id="twoFactorCode"
                  type="text"
                  maxLength={8}
                  placeholder={t.settings.twoFactorLoginCodePlaceholder}
                  value={twoFactorCode}
                  onChange={(e) => setTwoFactorCode(e.target.value.replace(/\D/g, ""))}
                  required
                  disabled={isLoading}
                  className="text-center text-2xl tracking-widest"
                  autoFocus
                />
                <p className="text-xs text-muted-foreground text-center">
                  {t.settings.twoFactorLoginBackupHint}
                </p>
              </div>
            </CardContent>
            <CardFooter className="flex flex-col gap-4 pt-6">
              <Button
                type="submit"
                className="w-full"
                size="lg"
                disabled={isLoading || twoFactorCode.length < 6}
              >
                {isLoading ? `${t.common.loading}` : t.settings.twoFactorLoginVerify}
              </Button>
              <Button
                type="button"
                variant="ghost"
                className="w-full gap-2"
                onClick={handleBackToLogin}
                disabled={isLoading}
              >
                <ArrowLeft className="h-4 w-4" />
                {t.settings.twoFactorLoginBack}
              </Button>
            </CardFooter>
          </form>
        ) : (
          <form onSubmit={handleSubmit}>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="email">{t.login.email}</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder={t.login.emailPlaceholder}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  disabled={isLoading}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">{t.login.password}</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder={t.login.passwordPlaceholder}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  disabled={isLoading}
                />
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="rememberDevice"
                  checked={rememberDevice}
                  onCheckedChange={(checked) => setRememberDevice(!!checked)}
                  disabled={isLoading}
                />
                <Label
                  htmlFor="rememberDevice"
                  className="text-sm font-normal cursor-pointer"
                >
                  {t.login.rememberDevice}
                </Label>
              </div>
            </CardContent>
            <CardFooter className="flex flex-col gap-4 pt-6">
              <Button
                type="submit"
                className="w-full"
                size="lg"
                disabled={isLoading}
              >
                {isLoading ? `${t.common.loading}` : t.login.loginButton}
              </Button>
              <div className="text-sm text-center">
                <a
                  href="/forgot-password"
                  className="text-primary hover:underline font-medium"
                >
                  {t.login.forgotPassword}
                </a>
              </div>
              <div className="text-sm text-center text-muted-foreground">
                {t.login.notRegistered}{" "}
                <a
                  href="/register"
                  className="text-primary hover:underline font-medium"
                >
                  {t.login.registerLink}
                </a>
              </div>
            </CardFooter>
          </form>
        )}
      </Card>
    </div>
  );
}
